/**
 * MedicationsPage — „Моите лекарства и добавки" + проверка на взаимодействия.
 *
 * Backend: /api/medications (CRUD, RxNorm търсене, внос от профила)
 *          /api/medications/interactions (CKD рулбук + openFDA листовки)
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle, Activity, Info, Loader2, Pill, Plus, Search, Trash2, Download, ShieldCheck, Bell, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";

const SEV = {
  action: { Icon: AlertTriangle, label: "ДЕЙСТВИЕ", color: "#EF4444" },
  watch: { Icon: Activity, label: "НАБЛЮДЕНИЕ", color: "#F59E0B" },
  info: { Icon: Info, label: "ИНФО", color: "#0EA5E9" },
};

const KIND_LABELS = { drug: "Лекарство", supplement: "Добавка", herbal: "Билка" };

export default function MedicationsPage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", kind: "drug", dose: "", frequency: "", since: "", rxcui: null, schedule: "" });
  const [suggest, setSuggest] = useState([]);
  const [autoHint, setAutoHint] = useState(null);
  const [duplicates, setDuplicates] = useState([]);
  const [todayPlan, setTodayPlan] = useState(null);
  const [pushState, setPushState] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");

  const loadToday = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/medications/today`);
      setTodayPlan(data);
    } catch { /* silent */ }
  }, [API]);

  const markTaken = async (d) => {
    try {
      await axios.post(`${API}/medications/${d.medication_id}/taken`, { time: d.time });
      loadToday();
    } catch { toast.error("Грешка при отбелязване."); }
  };

  const setSchedule = async (m) => {
    const current = (m.schedule_times || []).join(", ");
    const input = window.prompt("Часове за приём (например 08:00, 20:00):", current);
    if (input === null) return;
    const times = input.split(",").map((s) => s.trim()).filter(Boolean);
    try {
      await axios.put(`${API}/medications/${m.id}/schedule`, { times });
      toast.success("Часовете са запазени.");
      loadToday();
    } catch { toast.error("Грешка при запис на часовете."); }
  };

  const enablePush = async () => {
    try {
      const perm = await Notification.requestPermission();
      setPushState(perm);
      if (perm !== "granted") return;
      const { data } = await axios.get(`${API}/push/vapid-public-key`);
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: data.public_key || data.publicKey || data.key,
      });
      await axios.post(`${API}/push/subscribe`, sub.toJSON ? sub.toJSON() : sub);
      toast.success("Тихият сигнал е включен.");
    } catch {
      toast.error("Браузърът отказа известията. Проверете разрешенията.");
    }
  };

  const loadDuplicates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/medications/summary`);
      setDuplicates(data?.duplicates || []);
    } catch { setDuplicates([]); }
  }, [API]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/medications/interactions`, { timeout: 60000 });
      setData(data);
    } catch {
      toast.error("Грешка при зареждане на лекарствата.");
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { load(); loadToday(); loadDuplicates(); }, [load, loadToday, loadDuplicates]);

  useEffect(() => {
    const q = form.name.trim();
    if (q.length < 2) { setSuggest([]); return; }
    const t = setTimeout(async () => {
      try {
        // Първо българският указател (ИАЛ + централно разрешени), после RxNorm
        const { data: reg } = await axios.get(`${API}/medications/registry/search`, { params: { q } });
        if (reg?.items?.length) { setSuggest(reg.items); return; }
        const { data } = await axios.get(`${API}/medications/search`, { params: { q } });
        setSuggest(data?.items || []);
      } catch { setSuggest([]); }
    }, 400);
    return () => clearTimeout(t);
  }, [form.name, API]);

  const pickSuggestion = async (s) => {
    setSuggest([]);
    setForm((f) => ({ ...f, name: s.name, rxcui: s.rxcui || null }));
    if (!s.ingredient) return;   // RxNorm предложение — без автоматична доза
    try {
      const { data: sg } = await axios.get(`${API}/medications/dose-suggestion`, {
        params: { name: s.name, ingredient: s.ingredient, atc: s.atc,
                  strength: s.strength, form: s.form },
      });
      setForm((f) => ({
        ...f, name: s.name,
        dose: sg.dose || f.dose,
        frequency: sg.frequency || f.frequency,
        schedule: (sg.schedule_times || []).join(", "),
      }));
      setAutoHint([sg.frequency, sg.dose, sg.note_bg].filter(Boolean).join(" · "));
    } catch { setAutoHint(null); }
  };

  const add = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const { schedule, ...rest } = form;
      await axios.post(`${API}/medications`, {
        ...rest, name: form.name.trim(),
        schedule_times: schedule
          ? schedule.split(",").map((x) => x.trim()).filter(Boolean) : null,
      });
      setForm({ name: "", kind: "drug", dose: "", frequency: "", since: "", rxcui: null, schedule: "" });
      setSuggest([]);
      setAutoHint(null);
      loadDuplicates();
      toast.success("Добавено.");
      load();
    } catch {
      toast.error("Грешка при добавяне.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/medications/${id}`);
      load();
      loadDuplicates();
    } catch { toast.error("Грешка при изтриване."); }
  };

  const importFreetext = async () => {
    try {
      const { data } = await axios.post(`${API}/medications/import-freetext`);
      if (data.source_empty) { toast.info("В профила няма записани лекарства като текст."); return; }
      toast.success(`Внесени ${data.imported} записа от профила.`);
      load();
    } catch { toast.error("Грешка при вноса."); }
  };

  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const sub = isDark ? "text-slate-400" : "text-slate-600";
  const input = `w-full px-3 py-2 rounded-xl border text-sm ${
    isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-900"
  }`;

  const ctx = data?.renal_context || {};
  const findings = [...(data?.ckd_findings || []), ...(data?.label_findings || [])];

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`}
      data-testid="medications-page">
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        <div>
          <div className="flex items-center gap-2">
            <Pill className="w-6 h-6 text-violet-500" />
            <h1 className="text-2xl sm:text-3xl font-extrabold">Моите лекарства и добавки</h1>
          </div>
          <p className={`text-sm mt-1 ${sub}`}>
            Проверяваме комбинациите Ви спрямо бъбречния указател (KDIGO 2024) и официалните листовки на FDA.
            {ctx.egfr != null && <> Текущ eGFR: <strong>{ctx.egfr}</strong> mL/min{ctx.stage ? ` · стадий ${ctx.stage}` : ""}.</>}
          </p>
        </div>

        {/* Добавяне */}
        <div className={`p-4 rounded-2xl border ${card}`} data-testid="medications-add-card">
          <h2 className="text-sm font-bold mb-2">Добавете лекарство или добавка</h2>
          <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
            <div className="sm:col-span-2 relative">
              <input
                className={input}
                placeholder="Име (напр. Ibuprofen, Енап, Магнезий)"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value, rxcui: null }))}
                data-testid="medication-name-input"
              />
              {suggest.length > 0 && (
                <div className={`absolute z-20 mt-1 w-full rounded-xl border shadow-xl overflow-hidden ${
                  isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200"}`}
                  data-testid="medication-suggestions">
                  {suggest.map((s) => (
                    <button key={s.rxcui || `${s.name}-${s.strength}`}
                      onClick={() => pickSuggestion(s)}
                      className={`w-full text-left px-3 py-2 text-xs ${isDark ? "hover:bg-slate-800" : "hover:bg-slate-50"}`}
                      data-testid={`medication-suggestion-${s.rxcui || s.name}`}>
                      <Search className="w-3 h-3 inline mr-1 opacity-50" /> {s.name}
                      {s.ingredient && (
                        <span className="opacity-55">
                          {" "}· {s.ingredient}{s.strength ? ` · ${s.strength}` : ""}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <select className={input} value={form.kind}
              onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}
              data-testid="medication-kind-select">
              <option value="drug">Лекарство</option>
              <option value="supplement">Добавка</option>
              <option value="herbal">Билка</option>
            </select>
            <input className={input} placeholder="Доза" value={form.dose}
              onChange={(e) => setForm((f) => ({ ...f, dose: e.target.value }))}
              data-testid="medication-dose-input" />
            <input className={input} placeholder="Честота" value={form.frequency}
              onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value }))}
              data-testid="medication-frequency-input" />
            <button onClick={add} disabled={saving || !form.name.trim()}
              className="px-4 py-2 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-violet-500 to-fuchsia-600 disabled:opacity-50 inline-flex items-center justify-center gap-1"
              data-testid="medication-add-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Добави
            </button>
          </div>
          {autoHint && (
            <div className="mt-2 text-[11px] px-2.5 py-2 rounded-xl bg-violet-500/10 border-l-[3px] border-violet-500"
              data-testid="medication-auto-hint">
              Попълнено автоматично от указателя: {autoHint}. Променете, ако лекарят Ви е предписал друго.
            </div>
          )}
          {!!duplicates.length && (
            <div className="mt-3 space-y-2" data-testid="medication-duplicates">
              {duplicates.map((w) => (
                <div key={w.code} className="rounded-xl p-3 text-[12px]"
                  style={{
                    background: (w.severity === "high" ? "#EF4444" : "#F59E0B") + "14",
                    borderLeft: `3px solid ${w.severity === "high" ? "#EF4444" : "#F59E0B"}`,
                  }}
                  data-testid={`medication-duplicate-${w.code}`}>
                  <div className="font-bold text-[11px]"
                    style={{ color: w.severity === "high" ? "#EF4444" : "#F59E0B" }}>
                    {w.severity === "high" ? "Едно и също вещество" : "Една и съща група"}
                  </div>
                  <div className="mt-0.5">{w.message_bg}</div>
                  <div className="text-[11px] mt-1 opacity-80">→ {w.advice_bg}</div>
                </div>
              ))}
            </div>
          )}
          <button onClick={importFreetext}
            className={`mt-2 text-[11px] font-semibold inline-flex items-center gap-1 ${isDark ? "text-violet-300" : "text-violet-700"}`}
            data-testid="medication-import-btn">
            <Download className="w-3 h-3" /> Внеси лекарствата от профила ми (свободен текст)
          </button>
        </div>

        {/* Днешните дози + контролни изследвания */}
        {todayPlan && (
          <div className={`p-4 rounded-2xl border ${card}`} data-testid="medications-today-card">
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <h2 className="text-sm font-bold">Днес ({todayPlan.date})</h2>
              <div className="flex items-center gap-2">
                {pushState !== "granted" && (
                  <button onClick={enablePush}
                    className="text-[10px] font-bold px-2 py-1 rounded-full bg-violet-500/15 text-violet-500"
                    data-testid="medications-enable-push">
                    <Bell className="w-3 h-3 inline mr-0.5" /> Включи тих сигнал в браузъра
                  </button>
                )}
                <span className="text-[10px] opacity-60">
                  взети {todayPlan.taken_count}/{todayPlan.doses.length}
                </span>
              </div>
            </div>

            {todayPlan.doses.length === 0 ? (
              <div className={`text-[11px] ${sub}`} data-testid="medications-today-empty">
                Задайте часове за приём при лекарство от списъка по-долу (бутонът с часовника).
              </div>
            ) : (
              <div className="space-y-1.5">
                {todayPlan.doses.map((d) => (
                  <div key={`${d.medication_id}-${d.time}`}
                    className={`p-2 rounded-xl border flex items-center gap-2 ${
                      d.taken ? "border-emerald-500/40 bg-emerald-500/5"
                              : (isDark ? "border-white/10" : "border-gray-200")}`}
                    data-testid={`medication-dose-${d.medication_id}-${d.time}`}>
                    <span className="text-[11px] font-bold w-12">{d.time}</span>
                    <span className="text-[11px] flex-1 truncate">{d.name}{d.dose ? ` · ${d.dose}` : ""}</span>
                    <button onClick={() => markTaken(d)} disabled={d.taken}
                      className={`text-[10px] font-bold px-2 py-1 rounded-full ${
                        d.taken ? "bg-emerald-500/20 text-emerald-500" : "bg-violet-500/15 text-violet-500"}`}
                      data-testid={`medication-taken-${d.medication_id}-${d.time}`}>
                      {d.taken ? "взето" : "отбележи"}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {(todayPlan.lab_checks || []).length > 0 && (
              <div className="mt-3 space-y-1.5" data-testid="medications-lab-checks">
                {todayPlan.lab_checks.map((c) => (
                  <div key={c.marker + c.label_bg}
                    className={`p-2 rounded-xl text-[11px] ${c.overdue ? "bg-amber-500/10 text-amber-600 dark:text-amber-300" : (isDark ? "bg-white/[0.03]" : "bg-slate-50")}`}
                    data-testid={`medication-lab-check-${c.marker}`}>
                    <strong>{c.label_bg}</strong> — {c.why_bg}{" "}
                    {c.overdue
                      ? <em>Време е за ново изследване{c.days_since != null ? ` (последно преди ${c.days_since} дни)` : " (няма записано)"}.</em>
                      : <em>Последно преди {c.days_since} дни — наред е.</em>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Списък */}
        <div className={`p-4 rounded-2xl border ${card}`} data-testid="medications-list-card">
          <h2 className="text-sm font-bold mb-2">Моят списък ({data?.count ?? 0})</h2>
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
          ) : (data?.medications || []).length === 0 ? (
            <div className={`text-xs ${sub}`} data-testid="medications-empty">
              Все още няма записи. Добавете лекарствата и добавките, които приемате редовно.
            </div>
          ) : (
            <div className="space-y-1.5">
              {data.medications.map((m) => (
                <div key={m.id}
                  className={`p-2.5 rounded-xl border flex items-center gap-2 ${isDark ? "border-white/10" : "border-gray-200"}`}
                  data-testid={`medication-row-${m.id}`}>
                  <span className="text-xs font-bold flex-1 truncate">{m.name}</span>
                  {m.ingredient && (
                    <span className="text-[10px] opacity-60 hidden sm:inline" data-testid={`medication-ingredient-${m.id}`}>
                      {m.ingredient}
                    </span>
                  )}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isDark ? "bg-white/10" : "bg-slate-100"}`}>
                    {KIND_LABELS[m.kind] || m.kind}
                  </span>
                  {m.dose && <span className="text-[10px] opacity-60">{m.dose}</span>}
                  {m.frequency && <span className="text-[10px] opacity-60">{m.frequency}</span>}
                  <button onClick={() => setSchedule(m)} className="p-1 rounded-md text-violet-500 hover:bg-violet-500/10"
                    title="Часове за приём" data-testid={`medication-schedule-${m.id}`}>
                    <Clock className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => remove(m.id)} className="p-1 rounded-md text-rose-500 hover:bg-rose-500/10"
                    data-testid={`medication-delete-${m.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Находки */}
        <div className={`p-4 rounded-2xl border ${card}`} data-testid="medications-findings-card">
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <h2 className="text-sm font-bold">Възможни рискове и взаимодействия</h2>
            {data?.action_count > 0 && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-500"
                data-testid="medications-action-count">
                {data.action_count} изискват действие
              </span>
            )}
          </div>
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
          ) : findings.length === 0 ? (
            <div className="p-3 rounded-xl bg-emerald-500/10 text-emerald-500 text-xs inline-flex items-center gap-2"
              data-testid="medications-findings-empty">
              <ShieldCheck className="w-4 h-4" />
              {(data?.count || 0) === 0
                ? "Добавете лекарствата си, за да направим проверката."
                : "Не открихме ренално значими комбинации в този списък."}
            </div>
          ) : (
            <div className="space-y-2">
              {findings.map((f) => {
                const s = SEV[f.severity] || SEV.info;
                return (
                  <div key={f.id}
                    className={`p-3 rounded-xl border ${isDark ? "border-white/10 bg-white/[0.03]" : "border-gray-200 bg-slate-50"}`}
                    data-testid={`medication-finding-${f.id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-[13px] font-bold">{f.title_bg}</span>
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0"
                        style={{ background: s.color + "22", color: s.color }}>
                        {s.label}
                      </span>
                    </div>
                    <div className="text-[10px] opacity-60 mt-0.5">
                      {f.drugs?.join(" + ")} · {f.source}
                    </div>
                    {f.mechanism_bg && <div className="text-[11px] opacity-85 mt-1">{f.mechanism_bg}</div>}
                    {f.snippet_en && (
                      <div className={`text-[10px] mt-1 p-2 rounded-lg italic ${isDark ? "bg-black/30" : "bg-white"}`}>
                        „{f.snippet_en}“
                      </div>
                    )}
                    <div className="text-[11px] mt-1 font-semibold text-emerald-500">→ {f.action_bg}</div>
                    {f.evidence_bg && <div className="text-[9px] opacity-50 mt-1">{f.evidence_bg}</div>}
                  </div>
                );
              })}
            </div>
          )}
          <div className={`mt-3 text-[10px] ${sub}`}>{data?.disclaimer_bg}</div>
        </div>

        {/* По-безопасни алтернативи от българския указател */}
        {(data?.alternatives || []).length > 0 && (
          <div className={`p-4 rounded-2xl border ${card}`} data-testid="medications-alternatives-card">
            <h2 className="text-sm font-bold mb-2">По-безопасен избор при бъбречно засягане</h2>
            <div className="space-y-2">
              {data.alternatives.map((a, i) => (
                <div key={`${a.ingredient || "x"}-${i}`}
                  className={`p-2.5 rounded-xl border ${isDark ? "border-white/10 bg-white/[0.03]" : "border-gray-200 bg-slate-50"}`}
                  data-testid={`medication-alternative-${a.ingredient || a.name}`}>
                  <div className="text-[12px] font-bold">
                    {a.name} {a.ingredient && <span className="opacity-50 font-normal">({a.ingredient})</span>}
                  </div>
                  {a.risk_bg && <div className="text-[11px] opacity-80 mt-0.5">{a.risk_bg}</div>}
                  <div className="text-[11px] mt-1 font-semibold text-emerald-500">→ {a.alternative_bg}</div>
                </div>
              ))}
            </div>
            <div className={`mt-2 text-[10px] ${sub}`}>
              Указателят разпознава българските търговски имена от аптеката. Не спирайте лекарство сами —
              покажете тази страница на лекаря си.
            </div>
          </div>
        )}

        <button onClick={() => navigate("/dashboard/kidney-intelligence")}
          className="text-xs font-semibold text-cyan-500 hover:underline" data-testid="medications-back-khi">
          ← Към Kidney Health Intelligence
        </button>
      </div>
    </div>
  );
}
